import { dictionaries, type Locale, type TranslationKey, translate } from "@qualtio-workhub/i18n";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

type I18nContextValue = {
  locale: Locale;
  setLocale(locale: Locale): void;
  t(key: TranslationKey): string;
};

const storageKey = "qualtio-workhub-locale";
const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale() ?? "en");

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale(nextLocale) {
        setLocaleState(nextLocale);
        localStorage.setItem(storageKey, nextLocale);
      },
      t(key) {
        return translate(locale, key);
      },
    }),
    [locale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (context === null) {
    throw new Error("useI18n must be used within I18nProvider");
  }

  return context;
}

export function readStoredLocale(): Locale | null {
  const value = localStorage.getItem(storageKey);
  if (value === null) {
    return null;
  }

  return value in dictionaries ? (value as Locale) : null;
}
